import React from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, Typography, useMediaQuery, useTheme } from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import TimerIcon from '@mui/icons-material/Timer';

// Define sort options as constants for reuse
export const SORT_OPTIONS = {
  NAME_ASC: 'name_asc',
  NAME_DESC: 'name_desc',
  DATE_NEWEST: 'date_newest',
  DATE_OLDEST: 'date_oldest',
  DURATION_LONGEST: 'duration_longest',
  DURATION_SHORTEST: 'duration_shortest',
};

/**
 * A dropdown component for selecting sorting options
 * 
 * @param {Object} props - Component props
 * @param {string} props.value - Current sort option value
 * @param {Function} props.onChange - Callback when sort option changes
 * @returns {React.ReactElement} The SortSelector component
 */
const SortSelector = ({ value, onChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Get the label for a sort option
  const getSortLabel = (option) => {
    switch (option) {
      case SORT_OPTIONS.NAME_ASC:
        return 'Name (A to Z)';
      case SORT_OPTIONS.NAME_DESC:
        return 'Name (Z to A)';
      case SORT_OPTIONS.DATE_NEWEST:
        return 'Newest first';
      case SORT_OPTIONS.DATE_OLDEST:
        return 'Oldest first';
      case SORT_OPTIONS.DURATION_LONGEST:
        return 'Duration (longest)';
      case SORT_OPTIONS.DURATION_SHORTEST:
        return 'Duration (shortest)';
      default:
        return 'Sort by';
    }
  };

  // Get the icon for a sort option
  const getSortIcon = (option) => {
    switch (option) {
      case SORT_OPTIONS.NAME_ASC:
      case SORT_OPTIONS.NAME_DESC:
        return <SortByAlphaIcon fontSize="small" />;
      case SORT_OPTIONS.DATE_NEWEST:
      case SORT_OPTIONS.DATE_OLDEST:
        return <AccessTimeIcon fontSize="small" />;
      default:
        return <SortIcon fontSize="small" />;
    }
  };

  const handleChange = (event) => {
    if (onChange) {
      onChange(event.target.value);
    }
  };

  return (
    <FormControl 
      className="sort-selector"
      size="small" 
      variant="outlined"
      sx={{ 
        minWidth: isMobile ? '100%' : 180,
        maxWidth: isMobile ? '100%' : 'auto',
      }}
    >
      <InputLabel id="sort-select-label">Sort by</InputLabel>
      <Select
        labelId="sort-select-label"
        id="sort-select"
        value={value}
        onChange={handleChange}
        label="Sort by"
      >
        <MenuItem value={SORT_OPTIONS.NAME_ASC}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SortByAlphaIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body2">Name (A to Z)</Typography>
          </Box>
        </MenuItem>
        <MenuItem value={SORT_OPTIONS.NAME_DESC}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SortByAlphaIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body2">Name (Z to A)</Typography>
          </Box>
        </MenuItem>
        <MenuItem value={SORT_OPTIONS.DATE_NEWEST}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AccessTimeIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body2">Date (newest)</Typography>
          </Box>
        </MenuItem>
        <MenuItem value={SORT_OPTIONS.DATE_OLDEST}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AccessTimeIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body2">Date (oldest)</Typography>
          </Box>
        </MenuItem>
      </Select>
    </FormControl>
  );
};

export default SortSelector;